import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["children"];
import _regeneratorRuntime from "@babel/runtime/regenerator";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/es.array.map.js";
import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.array.iterator.js";
import "core-js/modules/es.promise.js";
import "core-js/modules/es.string.iterator.js";
import "core-js/modules/web.dom-collections.iterator.js";
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import groupBy from 'utils/groupBy';
import { hasPrivilege, isTeamManager } from 'utils/privileges';
import getUserId from 'utils/session/getUserId';
import config from 'utils/config';
import { COLLAPSED_NAVBAR_HEIGHT } from 'styles/globals';
import { BASIC } from 'styles/spacingPresets';
import Box from 'components/Box';
import Button from 'components/Button';
import Flex from 'components/Flex';
import Hidden from 'components/Hidden';
import Hint from 'components/Hint';
import InlineList from 'components/InlineList';
import { Menu, MenuButton, MenuItem, MenuItemDivider, MenuList } from 'components/Menu';
import StickyBox from 'components/StickyBox';
import TooltipButton from 'components/TooltipButton';
import { useUpdateMultipleAssets } from 'actions/assets';
import { useUpdateMultiplePlaylists } from 'actions/playlists';
import useBreakpoints from 'hooks/useBreakpoints';
import { wasContentItemDataChanged, getImplicitlySelectedItems, getSelectionCompletionState } from '../utils';
import { SaveChangedContentModal } from './SaveChangedContentModal';
import { checkIsItemEditable } from '../utils/checkIsItemEditable';
function ToolbarContent(_ref) {
  var children = _ref.children,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  return /*#__PURE__*/React.createElement(Flex, _extends({}, otherProps, {
    align: "center",
    width: "100%",
    minHeight: 50,
    py: "xxs"
  }), children);
}
export function Toolbar(_ref2) {
  var selectedItems = _ref2.selectedItems,
    selectedItemIds = _ref2.selectedItemIds,
    clearSelection = _ref2.clearSelection,
    reducerState = _ref2.reducerState,
    setItemIdToEdit = _ref2.setItemIdToEdit,
    stickyHeaderCompensation = _ref2.stickyHeaderCompensation,
    playlist = _ref2.playlist;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _useBreakpoints = useBreakpoints('xs'),
    isDesktop = _useBreakpoints.xs;
  var userId = getUserId();
  var checkCanRemoveAndSetOptional = useMemo(function () {
    return hasPrivilege('playlist_write') || playlist.ownerTeamId && isTeamManager(playlist.ownerTeamId) || playlist.allow_member_contributions && config.memberContributions && selectedItems.every(function (item) {
      var _item$author;
      return item.type !== 'section' && item.type !== 'playlist' && ((_item$author = item.author) === null || _item$author === void 0 ? void 0 : _item$author.id) === userId;
    });
  }, [playlist.allow_member_contributions, playlist.ownerTeamId, selectedItems, userId]);
  var _reducerState = _slicedToArray(reducerState, 2),
    _reducerState$ = _reducerState[0],
    content = _reducerState$.content,
    contentPendingDeletion = _reducerState$.contentPendingDeletion,
    mobileInteractionMode = _reducerState$.mobileInteractionMode,
    dispatch = _reducerState[1];
  var _useUpdateMultipleAss = useUpdateMultipleAssets({
      assets: contentPendingDeletion || []
    }),
    updateMultipleAssets = _useUpdateMultipleAss.updateMultipleAssets,
    isUpdatingMultipleAsset = _useUpdateMultipleAss.isLoading;
  var _useUpdateMultiplePla = useUpdateMultiplePlaylists({}),
    updateMultiplePlaylists = _useUpdateMultiplePla.updateMultiplePlaylists,
    isUpdatingMultiplePlaylists = _useUpdateMultiplePla.isLoading;

  // These are all items that are selected, but including all content items
  // inside of selected sections, and excluding the selected sections themselves
  var implicitlySelectedItems = useMemo(function () {
    return getImplicitlySelectedItems({
      selectedItemIds: selectedItemIds,
      content: content
    });
  }, [content, selectedItemIds]);
  function deleteSelectedItems() {
    var hasUnsavedChanges = implicitlySelectedItems.some(function (item) {
      return wasContentItemDataChanged(item);
    });
    if (hasUnsavedChanges) {
      dispatch({
        type: 'REQUEST_DELETE_CHANGED_CONTENT',
        ids: selectedItemIds
      });
    } else {
      dispatch({
        type: 'DELETE_CONTENT',
        ids: selectedItemIds
      });
      clearSelection();
    }
  }
  function confirmDeleteChangedContent(_x) {
    return _confirmDeleteChangedContent.apply(this, arguments);
  } // Completion state of the selected items
  function _confirmDeleteChangedContent() {
    _confirmDeleteChangedContent = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(ids) {
      var _groupBy, assets, playlists;
      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) switch (_context.prev = _context.next) {
          case 0:
            if (contentPendingDeletion) {
              _context.next = 2;
              break;
            }
            return _context.abrupt("return");
          case 2:
            _groupBy = groupBy(contentPendingDeletion, function (item) {
              if (item.type === 'playlist') {
                return 'playlists';
              }
              return 'assets';
            }), assets = _groupBy.assets, playlists = _groupBy.playlists;
            _context.next = 5;
            return Promise.all([assets && updateMultipleAssets(assets), playlists && updateMultiplePlaylists(playlists)]);
          case 5:
            dispatch({
              type: 'DELETE_CONTENT',
              ids: ids
            });
            clearSelection();
          case 7:
          case "end":
            return _context.stop();
        }
      }, _callee);
    }));
    return _confirmDeleteChangedContent.apply(this, arguments);
  }
  var selectionCompletionState = useMemo(function () {
    return getSelectionCompletionState(implicitlySelectedItems);
  }, [implicitlySelectedItems]);
  function makeSelectedItemsRequired() {
    dispatch({
      type: 'UPDATE_CONTENT',
      data: implicitlySelectedItems.map(function (item) {
        return {
          id: item.id,
          optional: false
        };
      })
    });
  }
  function makeSelectedItemsOptional() {
    dispatch({
      type: 'UPDATE_CONTENT',
      data: implicitlySelectedItems.map(function (item) {
        return {
          id: item.id,
          optional: true
        };
      })
    });
  }
  var hasOptionalButton = checkCanRemoveAndSetOptional && (selectionCompletionState === 'required' || selectionCompletionState === 'mixed');
  var hasRequiredButton = checkCanRemoveAndSetOptional && (selectionCompletionState === 'optional' || selectionCompletionState === 'mixed');
  var hasEditButton = useMemo(function () {
    return selectedItems.length === 1 && checkIsItemEditable({
      item: selectedItems[0],
      parentPlaylistMemberContributions: playlist.allow_member_contributions,
      parentPlaylistOwnerTeamId: playlist.ownerTeamId
    });
  }, [playlist.allow_member_contributions, playlist.ownerTeamId, selectedItems]);
  var primaryActions = [!isDesktop && {
    label: t('actions_move'),
    icon: 'move-vertical',
    onClick: function onClick() {
      return dispatch({
        type: 'SET_MOBILE_INTERACTION_MODE',
        mode: 'move'
      });
    }
  }, hasOptionalButton && {
    label: t('actions_make_optional'),
    icon: 'skip',
    onClick: makeSelectedItemsOptional
  }, hasRequiredButton && {
    label: t('actions_make_required'),
    icon: 'bang',
    onClick: makeSelectedItemsRequired
  }, hasEditButton && {
    label: t('actions_edit'),
    icon: 'edit',
    onClick: function onClick() {
      return setItemIdToEdit(selectedItemIds.at(0) || null);
    }
  }].filter(function (action) {
    return !!action;
  });
  var deleteAction = checkCanRemoveAndSetOptional ? {
    label: t('actions_remove'),
    icon: 'trash',
    onClick: deleteSelectedItems
  } : null;
  var defaultToolbarHint = content.length === 0 ? t('empty_playlist') : t('content_editor_select_to_move_or_edit');
  var hasSelectedItems = selectedItemIds.length > 0;
  var clearSelectionButton = /*#__PURE__*/React.createElement(TooltipButton, {
    iconOnly: true,
    icon: "x",
    variant: "transparent",
    size: "medium",
    title: t('actions_deselect_all'),
    onClick: clearSelection,
    ml: "-xs",
    mr: [undefined, 'xs'],
    breakpoints: "s"
  }, t('actions_deselect_all'));
  var toolbarContent = isDesktop ? /*#__PURE__*/React.createElement(ToolbarContent, {
    wrap: true,
    rowGap: "xxs",
    px: "m"
  }, hasSelectedItems && clearSelectionButton, /*#__PURE__*/React.createElement(Box, {
    shrink: true,
    pr: "m"
  }, hasSelectedItems ? t('generic_selection_status_short', {
    count: selectedItemIds.length
  }) : defaultToolbarHint), hasSelectedItems && /*#__PURE__*/React.createElement(Box, {
    shrink: true
  }, /*#__PURE__*/React.createElement(InlineList, {
    spacing: "xs"
  }, primaryActions.map(function (action) {
    return /*#__PURE__*/React.createElement(Button, {
      key: action.label,
      variant: "primary",
      size: "medium",
      onClick: action.onClick
    }, action.label);
  }), deleteAction && /*#__PURE__*/React.createElement(Button, {
    variant: "transparent",
    size: "medium",
    onClick: deleteAction.onClick
  }, deleteAction.label))), hasSelectedItems && /*#__PURE__*/React.createElement(Hidden, {
    below: "m",
    shrink: true,
    ml: "auto"
  }, /*#__PURE__*/React.createElement(Hint, {
    iconSpacing: "xxs",
    iconOffset: "-xxxs"
  }, t('content_editor_move_hint')))) : mobileInteractionMode === 'move' ? /*#__PURE__*/React.createElement(ToolbarContent, {
    gap: "m",
    px: "s"
  }, /*#__PURE__*/React.createElement(Box, {
    grow: true,
    shrink: true
  }, hasSelectedItems ? t('content_editor_move_hint') : t('content_editor_select_to_move')), /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement(Button, {
    variant: "primary",
    size: "medium",
    onClick: function onClick() {
      return dispatch({
        type: 'SET_MOBILE_INTERACTION_MODE',
        mode: 'default'
      });
    }
  }, t('actions_done')))) : /*#__PURE__*/React.createElement(ToolbarContent, {
    px: "s"
  }, hasSelectedItems && clearSelectionButton, /*#__PURE__*/React.createElement(Box, {
    shrink: true,
    pr: "m",
    mr: "auto"
  }, hasSelectedItems ? t('generic_selection_status_short', {
    count: selectedItemIds.length
  }) : defaultToolbarHint), hasSelectedItems && /*#__PURE__*/React.createElement(Box, {
    shrink: true
  }, /*#__PURE__*/React.createElement(Menu, {
    menuPlacement: "bottom-end"
  }, /*#__PURE__*/React.createElement(MenuButton, {
    variant: "primary",
    size: "medium",
    iconRight: "chevron"
  }, t('actions')), /*#__PURE__*/React.createElement(MenuList, null, primaryActions.map(function (action) {
    return /*#__PURE__*/React.createElement(MenuItem, {
      key: action.label,
      onClick: action.onClick,
      icon: action.icon
    }, action.label);
  }), deleteAction && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(MenuItemDivider, null), /*#__PURE__*/React.createElement(MenuItem, {
    onClick: deleteAction.onClick,
    icon: deleteAction.icon
  }, deleteAction.label))))));
  return /*#__PURE__*/React.createElement(StickyBox, {
    placement: "top",
    distance: stickyHeaderCompensation ? COLLAPSED_NAVBAR_HEIGHT : 0,
    backgroundColor: "shaded",
    mx: ['-s', 0],
    borderRadius: [undefined, 'small'],
    fontSize: BASIC.SMALL,
    breakpoints: BASIC.BREAKPOINTS
  }, toolbarContent, contentPendingDeletion && (contentPendingDeletion === null || contentPendingDeletion === void 0 ? void 0 : contentPendingDeletion.length) > 0 && /*#__PURE__*/React.createElement(SaveChangedContentModal, {
    contentPendingDeletion: contentPendingDeletion,
    onConfirm: function onConfirm() {
      return confirmDeleteChangedContent(selectedItemIds);
    },
    onClose: function onClose() {
      return dispatch({
        type: 'CANCEL_REQUEST_DELETE_CHANGED_CONTENT',
        ids: selectedItemIds
      });
    },
    onDiscard: function onDiscard() {
      return dispatch({
        type: 'DELETE_CONTENT',
        ids: selectedItemIds
      });
    },
    isLoading: isUpdatingMultipleAsset || isUpdatingMultiplePlaylists
  }));
}